import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const WebseitenBanner = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isMobile) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
    setMousePosition({ x, y });
  };

  return (
    <a href="https://web.aivinity.de" target="_blank" rel="noopener noreferrer" className="relative z-10">
      <motion.div
        className="relative flex items-center gap-2 px-3 py-1.5 rounded-full bg-card border border-electric-green/50 cursor-pointer overflow-visible shadow-lg"
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        onMouseMove={handleMouseMove}
        whileHover={!isMobile ? { scale: 1.02 } : {}}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
      >
        {/* Pulsing dot */}
        <motion.div
          className="w-2 h-2 rounded-full bg-electric-green"
          animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Text */}
        <span className="text-xs font-medium text-electric-green whitespace-nowrap">
          Jetzt neu: Webseiten
        </span>
        
        {/* Arrow */}
        <motion.span
          className="text-electric-green text-xs"
          animate={!isMobile && isHovered ? { x: [0, 3, 0] } : {}}
          transition={{ duration: 0.6, repeat: Infinity }}
        >
          →
        </motion.span>

        {/* 3D Browser Mockup - Desktop only */}
        {!isMobile && (
        <motion.div
            className="absolute top-full left-1/2 mt-2 pointer-events-none"
            initial={{ opacity: 0, y: -10, scale: 0.8 }}
            animate={isHovered ? { 
              opacity: 1, 
              y: 0, 
              scale: 1,
              rotateX: mousePosition.y * -10,
              rotateY: mousePosition.x * 10,
            } : { 
              opacity: 0, 
              y: -10, 
              scale: 0.8 
            }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            style={{ 
              transformStyle: "preserve-3d",
              perspective: "1000px",
              x: "-50%"
            }}
          >
            {/* Browser Window */}
            <div className="relative w-40 h-28 bg-card/95 backdrop-blur-xl rounded-lg border border-white/20 shadow-2xl overflow-hidden z-40">
              {/* Browser Header */}
              <div className="flex items-center gap-1.5 px-2 py-1.5 bg-white/5 border-b border-white/10">
                <div className="w-2 h-2 rounded-full bg-red-400/80" />
                <div className="w-2 h-2 rounded-full bg-yellow-400/80" />
                <div className="w-2 h-2 rounded-full bg-electric-green/80" />
                <div className="flex-1 mx-2 h-3 bg-white/10 rounded-full" />
              </div>
              
              {/* Browser Content */}
              <div className="p-2 space-y-1.5">
                <div className="h-2 bg-white/20 rounded w-3/4" />
                <div className="h-2 bg-white/10 rounded w-full" />
                <div className="h-2 bg-white/10 rounded w-5/6" />
                <div className="mt-2 flex gap-1">
                  <div className="h-6 w-12 bg-electric-green/30 rounded" />
                  <div className="h-6 flex-1 bg-white/10 rounded" />
                </div>
              </div>

              {/* Orbiting Particles */}
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-electric-green rounded-full"
                  animate={{
                    x: [0, 20, 0, -20, 0],
                    y: [0, -10, -20, -10, 0],
                    opacity: [0.3, 0.8, 0.3],
                  }}
                  transition={{
                    duration: 3,
                    delay: i * 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    left: `${20 + i * 25}%`,
                    top: "50%",
                  }}
                />
              ))}
            </div>

            {/* Floating Code Snippets */}
            <motion.div
              className="absolute -right-6 top-0 px-1.5 py-0.5 bg-blue-500/20 border border-blue-500/30 rounded text-[8px] text-blue-400 font-mono z-50"
              animate={{ y: [0, -5, 0], rotate: [0, 2, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              style={{ transform: "translateZ(30px)" }}
            >
              {"<div>"}
            </motion.div>
            <motion.div
              className="absolute -left-4 bottom-4 px-1.5 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded text-[8px] text-purple-400 font-mono z-50"
              animate={{ y: [0, 5, 0], rotate: [0, -2, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              style={{ transform: "translateZ(30px)" }}
            >
              {"</html>"}
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </a>
  );
};

export default WebseitenBanner;
